import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { createAppSlice } from './app-slice';
import { createSettingsSlice } from './settings-slice';
import { createCreatureSlice } from './creature-slice';
import { createLineageSlice } from './lineage-slice';
import { createWorldSlice } from './world-slice';
import { createCombatSlice } from './combat-slice';
import { createCloudSlice } from './cloud-slice';
import type { AppStore } from './types';

export type { AppStore } from './types';

export const useStore: UseBoundStore<StoreApi<AppStore>> = create<AppStore>()((set, get, api) => ({
  ...createAppSlice(set, get, api),
  ...createSettingsSlice(set, get, api),
  ...createCreatureSlice(set, get, api),
  ...createLineageSlice(set, get, api),
  ...createWorldSlice(set, get, api),
  ...createCombatSlice(set, get, api),
  ...createCloudSlice(set, get, api),
}));

export function getStore(): AppStore {
  return useStore.getState();
}

// Selectors
export const selectReady = (s: AppStore): boolean => s.ready;
export const selectSettings = (s: AppStore): AppStore['settings'] => s.settings;
export const selectLocale = (s: AppStore): AppStore['settings']['locale'] => s.settings.locale;
export const selectAudioSettings = (s: AppStore): AppStore['settings']['audio'] => s.settings.audio;
export const selectA11ySettings = (s: AppStore): AppStore['settings']['accessibility'] => s.settings.accessibility;
export const selectSaveMode = (s: AppStore): AppStore['saveMode'] => s.saveMode;
export const selectBootError = (s: AppStore): AppStore['bootError'] => s.bootError;
export const selectCreature = (s: AppStore): AppStore['creature'] => s.creature;
export const selectLightOn = (s: AppStore): boolean => s.lightOn;
export const selectLineage = (s: AppStore): AppStore['lineage'] => s.lineage;
export const selectNpcs = (s: AppStore): AppStore['npcs'] => s.npcs;
export const selectDiscoveredTypes = (s: AppStore): AppStore['discoveredTypes'] => s.discoveredTypes;
export const selectWorld = (s: AppStore): AppStore['world'] => s.world;
export const selectInWorld = (s: AppStore): boolean => s.inWorld;
export const selectInventory = (s: AppStore): AppStore['world']['inventory'] => s.world.inventory;
export const selectPlayerPos = (s: AppStore): { x: number; y: number } => ({ x: s.playerX, y: s.playerY });
export const selectActiveRun = (s: AppStore): AppStore['activeRun'] => s.activeRun;
export const selectInExpedition = (s: AppStore): boolean => s.inExpedition;
export const selectPlayerHp = (s: AppStore): { hp: number; max: number } => ({ hp: s.playerHp, max: s.playerMaxHp });
export const selectPendingPicks = (s: AppStore): AppStore['pendingPicks'] => s.pendingPicks;
export const selectCloudUser = (s: AppStore): AppStore['cloudUser'] => s.cloudUser;
export const selectCloudSyncStatus = (s: AppStore): AppStore['cloudSyncStatus'] => s.cloudSyncStatus;
export const selectCloudLastSyncAt = (s: AppStore): AppStore['cloudLastSyncAt'] => s.cloudLastSyncAt;
